import React, { useRef } from "react";
import QdsSignaturePad from "./QdsSignaturePad";
import "./QdsSignaturePadModal.style.css";
import { useTranslation } from "react-i18next";

interface Props {
  isOpen: boolean;
  pageIndex: number;
  coords: { x: number; y: number };
  width: number;
  height: number;
  onClose: () => void;
  onSave: (dataUrl: string, coords: { x: number; y: number }, pageIndex: number) => void;
}

const QdsSignaturePadModal: React.FC<Props> = ({
  isOpen,
  pageIndex,
  coords,
  width,
  height,
  onClose,
  onSave,
}) => {
  const sigPadRef = useRef<HTMLCanvasElement | null>(null);
  const { t } = useTranslation();

  const trimCanvas = (canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext("2d");
    if (!ctx) return canvas;

    const { width, height } = canvas;
    const imageData = ctx.getImageData(0, 0, width, height);
    const pixels = imageData.data;

    let minX = width,
      minY = height,
      maxX = 0,
      maxY = 0;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = (y * width + x) * 4;
        const alpha = pixels[i + 3];
        if (alpha > 0) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (maxX < minX || maxY < minY) return canvas; // no signature

    const trimmedWidth = maxX - minX + 1;
    const trimmedHeight = maxY - minY + 1;
    const trimmedCanvas = document.createElement("canvas");
    trimmedCanvas.width = trimmedWidth;
    trimmedCanvas.height = trimmedHeight;

    const trimmedCtx = trimmedCanvas.getContext("2d");
    if (!trimmedCtx) return canvas;

    trimmedCtx.putImageData(ctx.getImageData(minX, minY, trimmedWidth, trimmedHeight), 0, 0);

    return trimmedCanvas;
  };

  const handleTrim = () => {
    if (!sigPadRef.current) return;
    const trimmed = trimCanvas(sigPadRef.current);
    if (!trimmed) return;

    // Replace current canvas with trimmed image
    const ctx = sigPadRef.current.getContext("2d");
    if (!ctx) return;

    sigPadRef.current.width = trimmed.width;
    sigPadRef.current.height = trimmed.height;

    ctx.clearRect(0, 0, trimmed.width, trimmed.height);
    ctx.drawImage(trimmed, 0, 0);
  };

  const handleSave = () => {
    if (!sigPadRef.current) return;
    const original = sigPadRef.current.toDataURL("image/png");

    const offscreen = document.createElement("canvas");
    offscreen.width = width;
    offscreen.height = height;
    const ctx = offscreen.getContext("2d");
    const img = new Image();

    img.onload = () => {
      ctx?.clearRect(0, 0, width, height);
      ctx?.drawImage(img, 0, 0, width, height);
      const scaledDataUrl = offscreen.toDataURL("image/png");
      onSave(scaledDataUrl, coords, pageIndex);

      onClose();
    };
    img.src = original;
  };

  const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="qds-signature-backdrop" onClick={onBackdropClick}>
      <div className="qds-signature-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{t("WEB_QDS_SIGNATURE.DRAW_SIGNATURE")}</h3>
        <QdsSignaturePad canvasRef={sigPadRef} />
        <div className="qds-buttons-container">
          <button onClick={onClose} className="btn btn-neutral">
            {t("CANCEL_BUTTON")}
          </button>

          <button onClick={handleTrim} className="btn btn-outline-primary">
            {t("WEB_QDS_SIGNATURE.TRIM_SIGNATURE")}
          </button>
          <button onClick={handleSave} className="btn btn-primary">
            {t("SAVE_BUTTON")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QdsSignaturePadModal;
